import { AmountInputField, FormDivider, MonthlyPayment, SelectField } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { Button, Flex, Icon, SelectItem, View, tokens } from '@sapphire-react/core';
import { HouseFlatRoof, Subtract, Add, Sack } from '@sapphire/icons/react';
import { Fragment } from 'react';
import { type UseFormWatch, useFieldArray, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

enum KeptPropertyType {
  Apartment = 'APARTMENT',
  Villa = 'VILLA',
  HolidayHouse = 'HOLIDAY_HOME',
  Rental = 'RENTAL',
}

type Props = {
  control: Control<any>;
  watch: UseFormWatch<any>;
};

export function AddProperty({ control, watch }: Props) {
  const { t } = useTranslation();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'properties',
  });
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const propertyTypeRules = { required: t('personalInformation.inputs.property.requiredError') };
  const downpaymentRules = {
    required: t('personalInformation.inputs.remainingDebt.requiredError', { debtAmount: 0 }),
  };
  const monthlyPaymentRules = { required: t('personalInformation.inputs.monthlyPayment.requiredError') };

  return (
    <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
      {fields.map((field, index) => {
        const propertyType = watch(`properties.${index}.propertyType`);
        const monthlyPayment = watch(`properties.${index}.monthlyPayment`);

        return (
          <Fragment key={field.id}>
            <View>
              <Button
                variant={'text'}
                icon={<Subtract />}
                iconAlign={'left'}
                onPress={() => remove(index)}
                marginBottom={tokens.size.spacing2xs}
                data-test-id={`properties.${index}.remove`}
              >
                {t('removeProperty')}
              </Button>
              <View
                display={'flex'}
                flexDirection={'column'}
                key={field.id}
                gap={tokens.size.spacingMd}
                marginBottom={16}
              >
                <SelectField
                  control={control}
                  name={`properties.${index}.propertyType`}
                  placeholder={t('personalInformation.inputs.property.placeholder')}
                  rules={propertyTypeRules}
                  label={t('personalInformation.inputs.property.label')}
                  width={'100%'}
                  prefix={
                    <Icon size={inputSize}>
                      <HouseFlatRoof />
                    </Icon>
                  }
                >
                  <SelectItem key={KeptPropertyType.Villa}>
                    {t('personalInformation.inputs.property.options.villa')}
                  </SelectItem>
                  <SelectItem key={KeptPropertyType.Apartment}>
                    {t('personalInformation.inputs.property.options.apartment')}
                  </SelectItem>
                  <SelectItem key={KeptPropertyType.Rental}>
                    {t('personalInformation.inputs.property.options.rental')}
                  </SelectItem>
                  <SelectItem key={KeptPropertyType.HolidayHouse}>
                    {t('personalInformation.inputs.property.options.recreational')}
                  </SelectItem>
                </SelectField>
                {[KeptPropertyType.Apartment, KeptPropertyType.Villa, KeptPropertyType.HolidayHouse].includes(
                  propertyType,
                ) && (
                  <AmountInputField
                    control={control}
                    name={`properties.${index}.remainingDebt`}
                    label={t('personalInformation.inputs.remainingDebt.label')}
                    rules={downpaymentRules}
                    width={'100%'}
                    postfix={'kr'}
                    min={0}
                    max={99_999_999}
                    prefix={
                      <Icon size={inputSize}>
                        <Sack />
                      </Icon>
                    }
                  />
                )}
                {[KeptPropertyType.Apartment, KeptPropertyType.Rental].includes(propertyType) && (
                  <MonthlyPayment
                    control={control}
                    name={`properties.${index}.monthlyPayment`}
                    label={t('personalInformation.inputs.monthlyPayment.label')}
                    rules={monthlyPaymentRules}
                    fieldFormValue={monthlyPayment}
                  />
                )}
              </View>
            </View>
            <FormDivider marginBottom={8} marginTop={0} />
          </Fragment>
        );
      })}
      <Button
        variant={'text'}
        icon={<Add />}
        iconAlign={'left'}
        onPress={() => append({ propertyType: undefined, remainingDebt: undefined, monthlyPayment: undefined })}
        data-test-id={'add-property-button'}
      >
        {fields.length > 0 ? t('addOtherProperty') : t('addProperty')}
      </Button>
    </Flex>
  );
}
